import { formatDateString, userDisplayName } from "@cp/toolkit";
import {
  type OpportunitiesQuery,
  useOnOpportunityStateChangedSubscription,
  type useOpportunitiesQuery,
  useSubmissionChangedSubscription,
} from "@qw/graphql";
import { Collapsible, CollapsibleContent, CollapsibleTrigger, cn, Icon } from "@qw/ui";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { PRODUCTION_APP_ORIGINS } from "@/constants/app-origins";
import { useConversation } from "@/providers/use-conversation";
import { LinkOpportunityButton } from "../components/link-opportunity-button";
import { OpenOpportunityInClaudeButton } from "../components/open-opportunity-in-claude-button";
import { Row } from "../components/row";
import { UnlinkOpportunityButton } from "../components/unlink-opportunity-button";
import { ActiveSubmission } from "./active-submission";

export const ActiveOpportunity = ({
  liveUpdates = true,
  opportunity,
  refetch,
  showSubmissions = true,
}: {
  liveUpdates?: boolean;
  opportunity: OpportunitiesQuery["opportunities"][0];
  refetch: ReturnType<typeof useOpportunitiesQuery>["refetch"];
  showSubmissions?: boolean;
}) => {
  const { inboxConversation } = useConversation();
  const [searchParams] = useSearchParams();
  const opportunityIdParam = searchParams.get("opportunityId");
  const isOpportunityParamMatch = opportunityIdParam === opportunity.id;
  const scrolledOpportunityIdRef = useRef<string | null>(null);
  const opportunityRef = useRef<HTMLDivElement>(null);

  useOnOpportunityStateChangedSubscription({
    onData: () => refetch(),
    skip: !liveUpdates || !opportunity.id,
    variables: {
      id: opportunity.id || "",
    },
  });

  useSubmissionChangedSubscription({
    onData: () => refetch(),
    skip: !liveUpdates || !showSubmissions || !opportunity.id,
    variables: { opportunityId: opportunity.id || "" },
  });

  const {
    id,
    broker,
    insuredId,
    desiredEffectiveDate,
    selectedLinesOfBusiness,
    state,
    submissions: opportunitySubmissions,
  } = opportunity;

  const hasSubmissions = showSubmissions && opportunitySubmissions.length > 0;
  const selected = inboxConversation?.opportunities?.some((opp) => opp.id === opportunity.id);
  const [open, setOpen] = useState(() => liveUpdates && showSubmissions && (!!selected || isOpportunityParamMatch));

  const opportunitySubmissionIds = opportunitySubmissions?.map((s) => s.id) || [];
  const { submissions: conversationSubmissions = [] } = inboxConversation || {};
  const linkedSubmission = conversationSubmissions.find((s) => opportunitySubmissionIds.includes(s.id));

  useEffect(() => {
    if (liveUpdates && showSubmissions && selected) {
      setOpen(true);
    }
  }, [liveUpdates, selected, showSubmissions]);

  useEffect(() => {
    if (!isOpportunityParamMatch || !opportunityIdParam) {
      return;
    }

    if (!liveUpdates || !showSubmissions) {
      return;
    }

    setOpen(true);

    if (scrolledOpportunityIdRef.current === opportunityIdParam) {
      return;
    }

    scrolledOpportunityIdRef.current = opportunityIdParam;
    opportunityRef.current?.scrollIntoView({ block: "start" });
  }, [isOpportunityParamMatch, liveUpdates, opportunityIdParam, showSubmissions]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-0">
      <div ref={opportunityRef}>
        <Row
          state={state}
          href={`${PRODUCTION_APP_ORIGINS.brokerage}/insured/${insuredId}/plans/${id}`}
          button={
            <div className="flex items-center gap-0.5">
              <OpenOpportunityInClaudeButton opportunityId={id} />
              {inboxConversation ? (
                selected ? (
                  <UnlinkOpportunityButton conversationId={inboxConversation.id} opportunityId={id} />
                ) : (
                  <LinkOpportunityButton conversationId={inboxConversation.id} opportunityId={id} />
                )
              ) : null}
            </div>
          }
          className={cn(hasSubmissions && "relative z-2 rounded-b-none border-b-0")}
        >
          <div className="flex-2 space-y-1.5 truncate">
            <div className="font-medium truncate">{selectedLinesOfBusiness.join(", ")}</div>
            <div className="text-muted-foreground truncate">{userDisplayName(broker)}</div>
          </div>
          <div className="flex-1 space-y-1.5 truncate">
            <div className="font-medium truncate">{formatDateString(desiredEffectiveDate, "MMM d, yyyy")}</div>
          </div>
        </Row>
      </div>
      {hasSubmissions && (
        <div className="bg-card border border-border/60 overflow-hidden relative rounded-b z-1">
          <CollapsibleContent>
            <div className="divide-y divide-border/60">
              {opportunitySubmissions.map((submission) => (
                <ActiveSubmission
                  key={submission.id}
                  insuredId={insuredId}
                  linkedId={linkedSubmission?.id}
                  liveUpdates={liveUpdates}
                  refetch={refetch}
                  submission={submission}
                />
              ))}
            </div>
          </CollapsibleContent>
          <CollapsibleTrigger className="group bg-card data-[state=open]:border-border/60 data-[state=open]:border-t flex justify-center py-1 relative w-full z-0">
            <Icon
              icon="keyboard_arrow_down"
              className="bg-muted rounded-full text-xs transition-transform group-data-[state=open]:rotate-180"
            />
          </CollapsibleTrigger>
        </div>
      )}
    </Collapsible>
  );
};
